// Copyright (c) Microsoft Corporation.  All rights reserved.

/// <reference types="react" />
/// <reference types="react-dom" />

import React = require("react");
import { MessageBar, MessageBarType } from "OfficeFabric/MessageBar";
import { PrimaryButton} from "OfficeFabric/Button";
import { Spinner, SpinnerSize } from "OfficeFabric/Spinner";
import { IPickListSelection } from "VSSUI/PickList";
import * as Utils_String from "VSS/Utils/String";

import Contracts = require("TFS/DistributedTask/Contracts");
import { Component, IProps, IState } from "DistributedTaskControls/Common/Components/Base";
import { PanelComponent } from "DistributedTaskControls/Components/PanelComponent";
import { TeamProjectPickList } from "DistributedTaskControls/Components/TeamProjectPickList";
import { StoreManager } from "DistributedTaskControls/Common/Stores/StoreManager";
import Resources = require("DistributedTaskControls/Resources/TFS.Resources.DistributedTaskControls");
import { SharedProjectsPanelActionCreator } from "DistributedTaskControls/SharedControls/SharedProjectsPanel/SharedProjectsPanelActionCreator";
import { SharedProjectsPanelStore } from "DistributedTaskControls/SharedControls/SharedProjectsPanel/SharedProjectsPanelStore";
import { SharedProjectsPanelData } from "DistributedTaskControls/SharedControls/SharedProjectsPanel/SharedProjectsPanelModel";
import { ActionCreatorManager } from "DistributedTaskControls/Common/Actions/ActionCreatorManager";
import { ProjectReference } from "TFS/ServiceEndpoint/Contracts";

export interface ISharedProjectsPanelProps extends IProps {
    id: any;
    headerText: string;
    shareDescription: string;
    getSharedProjects: (id: any) => IPromise<Contracts.ProjectReference[]>;
    onClose: (isUpdated: boolean, isShared: boolean) => void;
    elementToFocusOnDismiss?: HTMLElement;
    shareWithProjects?: (id: any, projectReferences: ProjectReference[]) => IPromise<void>;
    unshareWithProjects?: (id: any, projectReferences: ProjectReference[]) => IPromise<void>;
}

export interface ISharedProjectsPanelState extends IState {
    errorMessage?: string;
    sharedProjects: Contracts.ProjectReference[];
    allProjects: Contracts.ProjectReference[];
    showPanel: boolean;
    dataLoaded: boolean;
}

export class SharedProjectsPanel extends Component<ISharedProjectsPanelProps, ISharedProjectsPanelState> {
    constructor(props: ISharedProjectsPanelProps) {
        super(props);

        this.state = {
            errorMessage: Utils_String.empty,
            sharedProjects: [],
            allProjects: [],
            showPanel: true,
            dataLoaded: false
        };

        this._sharedProjectsPanelStore = StoreManager.GetStore<SharedProjectsPanelStore>(SharedProjectsPanelStore);
        this._sharedProjectsPanelActionCreator = ActionCreatorManager.GetActionCreator<SharedProjectsPanelActionCreator>(SharedProjectsPanelActionCreator);

        this._sharedProjectsPanelActionCreator.getSharedProjectsData(this.props.id, this.props.getSharedProjects);
        this._sharedProjectsPanelStore.addChangedListener(this._onStoreChange);
    }

    public componentWillUnmount() {
        this._sharedProjectsPanelStore.removeChangedListener(this._onStoreChange);
    }

    public render(): JSX.Element {
        return (
            <PanelComponent
                showPanel={this.state.showPanel}
                headerText={this.props.headerText}
                hasCloseButton={true}
                onRenderFooterContent={this._onRenderFooterContent}
                isFooterAtBottom={true}
                isBlocking={true}
                onClose={this._onPanelClose}
                elementToFocusOnDismiss={this.props.elementToFocusOnDismiss}>
                <div className="shared-projects-panel">
                    {this._showErrorMessage()}
                    {this._getPanelContent()}
                </div>
            </PanelComponent>);
    }

    private _onRenderFooterContent = (): JSX.Element => {
        return (
            <PrimaryButton className="shared-projects-panel-button" onClick={this._onShareButtonClick} disabled={!(this._sharedProjectsPanelStore.isDirty())}>
                {Resources.ShareButtonText}
            </PrimaryButton>
        );
    }

    private _onPanelClose = () => {
        this._clearErrorMessage();
        let state = this._getState();
        let isShared = this._sharedProjectsPanelStore.isShared();
        let isUpdated = this._sharedProjectsPanelStore.isUpdated();
        state.showPanel = false;
        if (this.props.onClose) {
            this.props.onClose(isUpdated, isShared);
        }
        this.setState(state);
        this._sharedProjectsPanelActionCreator.handlePanelClose();
    }

    private _getPanelContent(): JSX.Element {
        let state = this._getState();
        if (!state.dataLoaded) {
            return (
                <Spinner size={SpinnerSize.small} />
            );
        } else {
            let allProjects: string[] = [];
            this.state.allProjects.forEach(project => {
                allProjects.push(project.name);
            });

            let sharedProjects: string[] = [];
            this.state.sharedProjects.forEach(project => {
                sharedProjects.push(project.name);
            });

            if (allProjects.length === 0) {
                return (
                    <div>
                        <span className="shared-projects-panel-description">{Resources.NoProjects}</span>
                    </div>
                );
            }

            return (
                <div>
                    <span className="shared-projects-panel-description">{this.props.shareDescription}</span>
                    <TeamProjectPickList items={allProjects} selectedProjectList={sharedProjects} onSelectionChanged={this._onSelectionChanged} />
                </div>
            );
        }
    }

    private _onShareButtonClick = () => {
        if (this._sharedProjectsPanelStore.isDirty()) {
            this._clearErrorMessage();

            let projectsAdded: Contracts.ProjectReference[] = this._sharedProjectsPanelStore.getNewSharedProjects();
            if (projectsAdded.length > 0) {
                this._sharedProjectsPanelActionCreator.shareWithProjects(this.props.id, projectsAdded, this.props.shareWithProjects);
            }

            let projectsRemoved: Contracts.ProjectReference[] = this._sharedProjectsPanelStore.getDeletedSharedProjects();
            if (projectsRemoved.length > 0) {
                this._sharedProjectsPanelActionCreator.unshareWithProjects(this.props.id, projectsRemoved, this.props.unshareWithProjects);
            }
        }
    }

    private _showErrorMessage(): JSX.Element {
        let state = this._getState();
        return !!(state.errorMessage) ?
            (<MessageBar messageBarType={MessageBarType.error} isMultiline={true} onDismiss={this._clearErrorMessage} dismissButtonAriaLabel={Resources.CloseButtonText}>
                {state.errorMessage}
            </MessageBar>) : null;
    }

    private _clearErrorMessage = () => {
        this._sharedProjectsPanelActionCreator.updateError(Utils_String.empty);
    }

    private _onSelectionChanged = (selection: IPickListSelection) => {
        let selectedProjects: string[] = selection.selectedItems || [];
        let projectReferences: Contracts.ProjectReference[] = [];

        selectedProjects.forEach((selectedProject) => {
            let projectReference = this.state.allProjects.find(project => project.name === selectedProject);

            if (projectReference) {
                projectReferences.push(projectReference);
            }
        });

        this._sharedProjectsPanelActionCreator.updateSharedProjects(projectReferences);
    }

    private _getState(): ISharedProjectsPanelState {
        let data: SharedProjectsPanelData = this._sharedProjectsPanelStore.getSharedProjectsData();
        let state: ISharedProjectsPanelState = this._getInitialState();
        if (data) {
            state.allProjects = data.allProjects;
            state.sharedProjects = data.sharedProjects;
            state.dataLoaded = true;
        }

        state.errorMessage = this._sharedProjectsPanelStore.getErrorMessage();
        state.showPanel = this.state.showPanel;
        return state;
    }

    private _onStoreChange = () => {
        let state = this._getState();
        state.dataLoaded = true;
        this.setState(state);
    }

    private _getInitialState(): ISharedProjectsPanelState {
        return {
            errorMessage: "",
            allProjects: [],
            sharedProjects: [],
            showPanel: true,
            dataLoaded: false
        } as ISharedProjectsPanelState;
    }

    private _sharedProjectsPanelStore: SharedProjectsPanelStore;
    private _sharedProjectsPanelActionCreator: SharedProjectsPanelActionCreator;
}
